import { sql } from 'drizzle-orm';
import {
  bigint,
  boolean,
  doublePrecision,
  index,
  integer,
  jsonb,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';

/**
 * Tables that exist only on the server: identity, sessions, one-time codes, patient-facing
 * links and the message queue. Clients never hold these.
 */

const ts = (name: string) => timestamp(name, { withTimezone: true, mode: 'string' });

export const users = pgTable(
  'users',
  {
    id: uuid('id').primaryKey(),
    name: text('name').notNull().default(''),
    /** Lower-cased. */
    email: text('email'),
    /** E.164. */
    phone: text('phone'),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    /** Set by the platform administrator to stop this person using LabView. */
    disabledAt: ts('disabled_at'),
    disabledReason: text('disabled_reason'),
  },
  (t) => [uniqueIndex('users_email').on(t.email), uniqueIndex('users_phone').on(t.phone)],
);

/**
 * Passwords, kept apart from `users` and `admin_users` so the lab-facing role and the lab backups
 * never see them. One row per account that chose a password: sign-in with a code keeps working
 * for everyone.
 */
export const passwords = pgTable(
  'passwords',
  {
    /** 'user' (a lab's person) or 'admin' (a platform administrator). */
    kind: text('kind').notNull(),
    accountId: uuid('account_id').notNull(),
    /** scrypt, with its own salt and cost, so the cost can be raised later. */
    hash: text('hash').notNull(),
    /** Wrong passwords in a row; zero after a right one. */
    failures: integer('failures').notNull().default(0),
    /** After too many wrong passwords, sign-in by password waits until this time. */
    lockedUntil: ts('locked_until'),
    setAt: ts('set_at').notNull().default(sql`now()`),
  },
  (t) => [primaryKey({ columns: [t.kind, t.accountId] })],
);

/**
 * The templates the LabView team keeps to send to labs. A lab that is sent one gets its own copy
 * (a normal template it can edit), so changing or removing one here never touches a lab's copy.
 * Closed to the lab-facing database role.
 */
export const platformTemplates = pgTable(
  'platform_templates',
  {
    id: uuid('id').primaryKey(),
    name: text('name').notNull(),
    department: text('department'),
    doc: jsonb('doc').notNull(),
    tests: integer('tests').notNull().default(0),
    /** The administrator who added it. */
    createdBy: uuid('created_by'),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    archivedAt: ts('archived_at'),
  },
  (t) => [index('platform_templates_created').on(t.createdAt)],
);

/** Which lab was sent which library template, and the lab's copy of it. */
export const platformTemplateDeliveries = pgTable(
  'platform_template_deliveries',
  {
    templateId: uuid('template_id').notNull(),
    orgId: uuid('org_id').notNull(),
    /** The template row created in the lab. */
    labTemplateId: uuid('lab_template_id').notNull(),
    sentBy: uuid('sent_by'),
    sentAt: ts('sent_at').notNull().default(sql`now()`),
  },
  (t) => [
    primaryKey({ columns: [t.templateId, t.orgId] }),
    index('platform_template_deliveries_org').on(t.orgId),
  ],
);

export const sessions = pgTable(
  'sessions',
  {
    id: uuid('id').primaryKey(),
    userId: uuid('user_id').notNull(),
    /** SHA-256 of the token; the token itself is never stored. */
    tokenHash: text('token_hash').notNull(),
    orgId: uuid('org_id'),
    deviceId: uuid('device_id'),
    userAgent: text('user_agent'),
    /** Set when an administrator opened the lab as this person to help them (a support session). */
    impersonatedBy: uuid('impersonated_by'),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    lastUsedAt: ts('last_used_at').notNull().default(sql`now()`),
    expiresAt: ts('expires_at').notNull(),
  },
  (t) => [uniqueIndex('sessions_token').on(t.tokenHash), index('sessions_user').on(t.userId)],
);

/** One-time codes for sign-in and for patients opening a report. Only an HMAC is stored. */
export const otpChallenges = pgTable(
  'otp_challenges',
  {
    id: uuid('id').primaryKey(),
    purpose: text('purpose').notNull(),
    /** Normalised email or E.164 phone the code was sent to. */
    identifier: text('identifier').notNull(),
    channel: text('channel').notNull(),
    otpHmac: text('otp_hmac').notNull(),
    /** What the code unlocks, e.g. { reportId } for patient access. */
    context: jsonb('context').$type<Record<string, unknown>>().notNull().default({}),
    attempts: integer('attempts').notNull().default(0),
    ip: text('ip'),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    expiresAt: ts('expires_at').notNull(),
    consumedAt: ts('consumed_at'),
  },
  (t) => [
    index('otp_identifier').on(t.identifier, t.createdAt),
    index('otp_ip').on(t.ip, t.createdAt),
  ],
);

export const invites = pgTable(
  'invites',
  {
    id: uuid('id').primaryKey(),
    orgId: uuid('org_id').notNull(),
    siteId: uuid('site_id'),
    /** Normalised email or E.164 phone. The invite is claimed when this person signs in. */
    identifier: text('identifier').notNull(),
    displayName: text('display_name').notNull().default(''),
    roles: text('roles').array().notNull().default(sql`'{}'::text[]`),
    createdBy: uuid('created_by').notNull(),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    expiresAt: ts('expires_at').notNull(),
    acceptedAt: ts('accepted_at'),
  },
  (t) => [index('invites_identifier').on(t.identifier)],
);

/**
 * A link a patient or doctor opens without an OTP: the delivered link (72 hours) and the
 * "share with my doctor" link (7 days). Only a hash of the token is stored.
 */
export const shareLinks = pgTable(
  'share_links',
  {
    id: uuid('id').primaryKey(),
    orgId: uuid('org_id').notNull(),
    kind: text('kind').notNull(),
    reportId: uuid('report_id'),
    visitId: uuid('visit_id'),
    tokenHash: text('token_hash').notNull(),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    expiresAt: ts('expires_at').notNull(),
    revokedAt: ts('revoked_at'),
  },
  (t) => [uniqueIndex('share_links_token').on(t.tokenHash)],
);

/** Every look at a patient's report by someone other than the lab. The lab can read it. */
export const accessLog = pgTable(
  'access_log',
  {
    id: uuid('id').primaryKey(),
    orgId: uuid('org_id').notNull(),
    reportId: uuid('report_id'),
    visitId: uuid('visit_id'),
    kind: text('kind').notNull(),
    ip: text('ip'),
    userAgent: text('user_agent'),
    at: ts('at').notNull().default(sql`now()`),
  },
  (t) => [index('access_log_report').on(t.reportId, t.at)],
);

/**
 * Transactional outbox: side effects (WhatsApp, email) are written in the same transaction
 * as the change that causes them, and delivered by a worker.
 */
export const messageOutbox = pgTable(
  'message_outbox',
  {
    id: uuid('id').primaryKey(),
    orgId: uuid('org_id'),
    kind: text('kind').notNull(),
    payload: jsonb('payload').$type<Record<string, unknown>>().notNull(),
    status: text('status').notNull().default('pending'),
    attempts: integer('attempts').notNull().default(0),
    lastError: text('last_error'),
    runAfter: ts('run_after').notNull().default(sql`now()`),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    sentAt: ts('sent_at'),
  },
  (t) => [index('message_outbox_due').on(t.status, t.runAfter)],
);

/** Hands out lab numbers per site and year, to online callers and to device blocks. */
export const numberCounters = pgTable(
  'number_counters',
  {
    orgId: uuid('org_id').notNull(),
    siteId: uuid('site_id').notNull(),
    year: integer('year').notNull(),
    next: bigint('next', { mode: 'number' }).notNull().default(1),
  },
  (t) => [primaryKey({ columns: [t.orgId, t.siteId, t.year] })],
);

// ---- the platform administrator's side ---------------------------------------------------
// These tables belong to the person who runs LabView itself, not to any lab. The limited
// database role used for lab data has no access to them (see migration 0003).

/** What the platform administrator has decided about one lab. */
export const orgPlatform = pgTable('org_platform', {
  orgId: uuid('org_id').primaryKey(),
  suspendedAt: ts('suspended_at'),
  suspendedReason: text('suspended_reason'),
  planId: uuid('plan_id'),
  notes: text('notes'),
  /** When the lab's current plan began, and when it runs out. Null end = it does not run out. */
  planStartedAt: ts('plan_started_at'),
  planExpiresAt: ts('plan_expires_at'),
  /** For a subscription: which period was bought (monthly or yearly). */
  planPeriod: text('plan_period'),
  /** Pay-per-report wallet, in paise. May go below zero, down to minus the credit limit. */
  walletPaise: bigint('wallet_paise', { mode: 'number' }).notNull().default(0),
  /** How far below zero the wallet may go while the lab keeps making reports. */
  creditLimitPaise: bigint('credit_limit_paise', { mode: 'number' }).notNull().default(0),
  /** What one report costs this lab, copied from the plan when it was assigned. */
  reportPricePaise: bigint('report_price_paise', { mode: 'number' }).notNull().default(0),
  updatedAt: ts('updated_at').notNull().default(sql`now()`),
});

/** Every change to a lab's wallet: credit added, a report charged, a correction. */
export const walletEntries = pgTable(
  'wallet_entries',
  {
    id: uuid('id').primaryKey(),
    orgId: uuid('org_id').notNull(),
    at: ts('at').notNull().default(sql`now()`),
    /** topup, report or adjustment. */
    kind: text('kind').notNull(),
    /** Positive adds to the wallet, negative takes from it. */
    amountPaise: bigint('amount_paise', { mode: 'number' }).notNull(),
    balanceAfterPaise: bigint('balance_after_paise', { mode: 'number' }).notNull(),
    reportId: uuid('report_id'),
    note: text('note').notNull().default(''),
    createdBy: uuid('created_by'),
  },
  (t) => [
    index('wallet_entries_org_at').on(t.orgId, t.at),
    // A report is charged once, however many times it is synced.
    uniqueIndex('wallet_entries_report').on(t.reportId).where(sql`kind = 'report'`),
  ],
);

/** The plans shown on the About page. Edited only by the platform administrator. */
export const plans = pgTable('plans', {
  id: uuid('id').primaryKey(),
  /** subscription (monthly or yearly price), validity (a fixed number of days) or per_report (wallet). */
  kind: text('kind').notNull().default('subscription'),
  /** Validity plans: how many days it lasts, and what it costs once (0 = free). Whole rupees. */
  validityDays: integer('validity_days'),
  priceOnce: integer('price_once').notNull().default(0),
  /** Pay-per-report plans: the price of one report, and the usual credit limit. Paise. */
  perReportPaise: bigint('per_report_paise', { mode: 'number' }).notNull().default(0),
  creditLimitPaise: bigint('credit_limit_paise', { mode: 'number' }).notNull().default(0),
  /** Given to every new lab automatically. At most one plan is the default. */
  defaultForNewLabs: boolean('default_for_new_labs').notNull().default(false),
  name: text('name').notNull(),
  description: text('description').notNull().default(''),
  /** Whole rupees. */
  priceMonthly: integer('price_monthly').notNull().default(0),
  priceYearly: integer('price_yearly').notNull().default(0),
  features: text('features').array().notNull().default(sql`'{}'::text[]`),
  /** Shown to labs as a limit; not enforced. Null = no stated limit. */
  maxUsers: integer('max_users'),
  highlight: boolean('highlight').notNull().default(false),
  active: boolean('active').notNull().default(true),
  sort: integer('sort').notNull().default(0),
  createdAt: ts('created_at').notNull().default(sql`now()`),
  updatedAt: ts('updated_at').notNull().default(sql`now()`),
});

/** A message from the platform administrator to every lab, or to one. */
export const broadcasts = pgTable(
  'broadcasts',
  {
    id: uuid('id').primaryKey(),
    title: text('title').notNull(),
    body: text('body').notNull().default(''),
    /** info, payment or urgent: how it looks. */
    kind: text('kind').notNull().default('info'),
    /** Null = every lab. */
    orgId: uuid('org_id'),
    createdBy: uuid('created_by'),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    expiresAt: ts('expires_at'),
    withdrawnAt: ts('withdrawn_at'),
  },
  (t) => [index('broadcasts_created').on(t.createdAt)],
);

/** Single values the platform administrator sets: backup destination, support details. */
export const platformSettings = pgTable('platform_settings', {
  key: text('key').primaryKey(),
  value: jsonb('value').$type<unknown>().notNull(),
  updatedAt: ts('updated_at').notNull().default(sql`now()`),
});

/** One run of the backup of every lab to the administrator's server. */
export const backupRuns = pgTable(
  'backup_runs',
  {
    id: uuid('id').primaryKey(),
    startedAt: ts('started_at').notNull().default(sql`now()`),
    finishedAt: ts('finished_at'),
    /** running, ok, partial (some labs failed) or failed. */
    status: text('status').notNull().default('running'),
    trigger: text('trigger').notNull().default('schedule'),
    labs: integer('labs').notNull().default(0),
    files: integer('files').notNull().default(0),
    bytes: bigint('bytes', { mode: 'number' }).notNull().default(0),
    error: text('error'),
  },
  (t) => [index('backup_runs_started').on(t.startedAt)],
);

/**
 * A lab owner asking for their lab to be deleted. An administrator approves or declines it in the
 * console. The row outlives the lab, so it has no `org_id` column and keeps the lab's name.
 */
export const labDeletionRequests = pgTable(
  'lab_deletion_requests',
  {
    id: uuid('id').primaryKey(),
    labId: uuid('lab_id').notNull(),
    labName: text('lab_name').notNull(),
    requestedBy: uuid('requested_by').notNull(),
    requestedByName: text('requested_by_name').notNull().default(''),
    reason: text('reason').notNull().default(''),
    /** pending, approved, declined or cancelled. */
    status: text('status').notNull().default('pending'),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    decidedAt: ts('decided_at'),
    decidedBy: uuid('decided_by'),
    decisionNote: text('decision_note'),
  },
  (t) => [
    index('lab_deletion_requests_lab').on(t.labId),
    index('lab_deletion_requests_status').on(t.status),
  ],
);

/** What the platform administrator did, for their own accountability. Append only. */
export const platformAudit = pgTable(
  'platform_audit',
  {
    id: uuid('id').primaryKey(),
    at: ts('at').notNull().default(sql`now()`),
    actor: uuid('actor'),
    action: text('action').notNull(),
    target: text('target'),
    detail: jsonb('detail').$type<Record<string, unknown>>().notNull().default({}),
  },
  (t) => [index('platform_audit_at').on(t.at)],
);

/**
 * The people who run LabView. A separate set of tables from `users` and `sessions`: a lab
 * account can never become an administrator, and an administrator's sign-in is not a lab sign-in.
 * Created by the `admin:users` command on the server, never from a web page.
 */
export const adminUsers = pgTable(
  'admin_users',
  {
    id: uuid('id').primaryKey(),
    name: text('name').notNull().default(''),
    /** Lower-cased. */
    email: text('email'),
    /** E.164. */
    phone: text('phone'),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    disabledAt: ts('disabled_at'),
  },
  (t) => [
    uniqueIndex('admin_users_email').on(t.email),
    uniqueIndex('admin_users_phone').on(t.phone),
  ],
);

export const adminSessions = pgTable(
  'admin_sessions',
  {
    id: uuid('id').primaryKey(),
    adminId: uuid('admin_id').notNull(),
    /** SHA-256 of the token; the token itself is never stored. */
    tokenHash: text('token_hash').notNull(),
    userAgent: text('user_agent'),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    lastUsedAt: ts('last_used_at').notNull().default(sql`now()`),
    expiresAt: ts('expires_at').notNull(),
  },
  (t) => [
    uniqueIndex('admin_sessions_token').on(t.tokenHash),
    index('admin_sessions_admin').on(t.adminId),
  ],
);

/** One-time codes for administrator sign-in. Same shape as `otp_challenges`, kept apart. */
export const adminOtpChallenges = pgTable(
  'admin_otp_challenges',
  {
    id: uuid('id').primaryKey(),
    purpose: text('purpose').notNull(),
    identifier: text('identifier').notNull(),
    channel: text('channel').notNull(),
    otpHmac: text('otp_hmac').notNull(),
    context: jsonb('context').$type<Record<string, unknown>>().notNull().default({}),
    attempts: integer('attempts').notNull().default(0),
    ip: text('ip'),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    expiresAt: ts('expires_at').notNull(),
    consumedAt: ts('consumed_at'),
  },
  (t) => [
    index('admin_otp_identifier').on(t.identifier, t.createdAt),
    index('admin_otp_ip').on(t.ip, t.createdAt),
  ],
);

// ---- public listing and bookings -----------------------------------------------------------
// A lab that opts in is listed in the LabView app for anyone to find, and people can book a
// visit or a home collection with it. Server-only: the lab edits them online, the public reads
// them through the API, and nothing here goes to the lab's devices by sync.

/** One lab's public page. Nothing shows publicly until `enabled` is turned on. */
export const labListings = pgTable(
  'lab_listings',
  {
    orgId: uuid('org_id').primaryKey(),
    enabled: boolean('enabled').notNull().default(false),
    name: text('name').notNull().default(''),
    about: text('about').notNull().default(''),
    address: text('address').notNull().default(''),
    area: text('area').notNull().default(''),
    city: text('city').notNull().default(''),
    pincode: text('pincode').notNull().default(''),
    phone: text('phone').notNull().default(''),
    whatsapp: text('whatsapp').notNull().default(''),
    email: text('email').notNull().default(''),
    logoFileId: uuid('logo_file_id'),
    /**
     * Which details follow the lab's report settings instead of their own value here:
     * { name, address, phone, email, logo }. On by default, so a lab starts from what it prints.
     */
    sameAsReport: jsonb('same_as_report')
      .$type<Record<string, boolean>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    /** The Google Maps link the lab pastes; people open it for directions. */
    mapsUrl: text('maps_url').notNull().default(''),
    /** Read from the maps link, for "near me". */
    lat: doublePrecision('lat'),
    lng: doublePrecision('lng'),
    homeCollection: boolean('home_collection').notNull().default(false),
    homeCollectionFeePaise: integer('home_collection_fee_paise').notNull().default(0),
    /** How far the lab goes for a home collection, in km; 0 = it does not say. */
    homeCollectionKm: integer('home_collection_km').notNull().default(0),
    reportDelivery: boolean('report_delivery').notNull().default(false),
    reportDeliveryFeePaise: integer('report_delivery_fee_paise').notNull().default(0),
    acceptsBookings: boolean('accepts_bookings').notNull().default(true),
    open24h: boolean('open_24h').notNull().default(false),
    /** Seven entries, Monday first: { open, from: 'HH:MM', to: 'HH:MM' }. */
    hours: jsonb('hours')
      .$type<{ open: boolean; from: string; to: string }[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    /** Days the lab is closed although the week says open (holidays), 'YYYY-MM-DD'. */
    closedDates: jsonb('closed_dates').$type<string[]>().notNull().default(sql`'[]'::jsonb`),
    /** Length of a booking slot in minutes. */
    slotMinutes: integer('slot_minutes').notNull().default(30),
    /**
     * How many people one slot takes: visits at the lab, and home collections (limited by the
     * collectors the lab has). 0 = no limit.
     */
    slotCapacity: integer('slot_capacity').notNull().default(5),
    homeSlotCapacity: integer('home_slot_capacity').notNull().default(2),
    facilities: jsonb('facilities').$type<string[]>().notNull().default(sql`'[]'::jsonb`),
    hidePrices: boolean('hide_prices').notNull().default(false),
    /** Tests left off the public page; every other active test shows by itself. */
    hiddenTemplates: jsonb('hidden_templates')
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    /** Tests shown without their price. */
    hiddenPrices: jsonb('hidden_prices').$type<string[]>().notNull().default(sql`'[]'::jsonb`),
    updatedAt: ts('updated_at').notNull().default(sql`now()`),
  },
  (t) => [index('lab_listings_city').on(t.enabled, t.city)],
);

/** A visit or home collection someone asked a lab for. The lab approves or declines it. */
export const bookings = pgTable(
  'bookings',
  {
    id: uuid('id').primaryKey(),
    orgId: uuid('org_id').notNull(),
    /** Short code people read out at the counter. */
    code: text('code').notNull(),
    /** The verified mobile number (E.164) of the person who booked. */
    contact: text('contact').notNull(),
    patientName: text('patient_name').notNull(),
    patientAge: text('patient_age').notNull().default(''),
    patientSex: text('patient_sex').notNull().default(''),
    /** 'visit' (at the lab) or 'home' (collection at home). */
    kind: text('kind').notNull(),
    tests: jsonb('tests')
      .$type<{ templateId: string; name: string; pricePaise: number | null }[]>()
      .notNull(),
    totalPaise: integer('total_paise').notNull().default(0),
    feePaise: integer('fee_paise').notNull().default(0),
    reportDelivery: boolean('report_delivery').notNull().default(false),
    date: text('date').notNull(),
    time: text('time').notNull(),
    address: text('address').notNull().default(''),
    note: text('note').notNull().default(''),
    /** pending, approved, declined, cancelled (by the person), done. */
    status: text('status').notNull().default('pending'),
    labComment: text('lab_comment').notNull().default(''),
    cancelReason: text('cancel_reason').notNull().default(''),
    decidedBy: uuid('decided_by'),
    decidedAt: ts('decided_at'),
    createdAt: ts('created_at').notNull().default(sql`now()`),
    updatedAt: ts('updated_at').notNull().default(sql`now()`),
  },
  (t) => [
    index('bookings_org_date').on(t.orgId, t.date),
    index('bookings_contact').on(t.contact, t.createdAt),
    uniqueIndex('bookings_code').on(t.code),
  ],
);

/** Where to send push notifications: a lab member's phone, or a public user's phone. */
export const pushTokens = pgTable(
  'push_tokens',
  {
    token: text('token').notNull(),
    /**
     * 'member' (lab staff, by user and lab) or 'public' (by verified mobile number). One phone can be
     * both: a lab's person who also books for their family.
     */
    audience: text('audience').notNull(),
    userId: uuid('user_id'),
    orgId: uuid('org_id'),
    contact: text('contact'),
    updatedAt: ts('updated_at').notNull().default(sql`now()`),
  },
  (t) => [
    primaryKey({ columns: [t.token, t.audience] }),
    index('push_tokens_org').on(t.orgId),
    index('push_tokens_contact').on(t.contact),
  ],
);
